import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff, FileText } from 'lucide-react';
import { Job } from '../types/Job';
import { getJobs, deleteJob, updateJob } from '../utils/storage';

const Admin: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    const jobsData = await getJobs();
    setJobs(jobsData);
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirm === id) {
      await deleteJob(id);
      await loadJobs();
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
    }
  };

  const handleToggleActive = async (id: string, currentStatus: boolean) => {
    await updateJob(id, { isActive: !currentStatus });
    await loadJobs();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'full-time': 'Tiempo Completo',
      'part-time': 'Tiempo Parcial',
      'contract': 'Contrato',
      'temporary': 'Temporal'
    };
    return labels[type] || type;
  };

  return (
    <div>
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Panel de Administración</h1>
          <p className="text-xl">Gestiona las ofertas de trabajo</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="mb-8 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-gray-800">
              Ofertas de Trabajo ({jobs.length})
            </h2>
            <button
              onClick={() => navigate('/admin/job/new')}
              className="bg-red-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-red-700 transition flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Nueva Oferta</span>
            </button>
          </div>

          {jobs.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-600 text-lg mb-4">
                No hay ofertas de trabajo creadas todavía.
              </p>
              <button
                onClick={() => navigate('/admin/job/new')}
                className="inline-block bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition"
              >
                Crear Primera Oferta
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Título / Empresa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ubicación
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Publicado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobs.map(job => (
                      <tr key={job.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <p className="font-semibold text-gray-900">{job.title}</p>
                            <p className="text-sm text-gray-600">{job.company}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-gray-900">{job.location}</p>
                          <p className="text-sm text-gray-600">{job.country}</p>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {getTypeLabel(job.type)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {formatDate(job.datePosted)}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => handleToggleActive(job.id, job.isActive)}
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                              job.isActive
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {job.isActive ? (
                              <>
                                <Eye className="w-3 h-3 mr-1" />
                                Activa
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-3 h-3 mr-1" />
                                Inactiva
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => navigate(`/admin/job/${job.id}`)}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <Edit2 className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDelete(job.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                          {deleteConfirm === job.id && (
                            <div className="mt-2 text-xs">
                              <p className="text-red-600 mb-1">¿Confirmar eliminación?</p>
                              <button
                                onClick={() => handleDelete(job.id)}
                                className="text-red-600 underline mr-2"
                              >
                                Sí
                              </button>
                              <button
                                onClick={() => setDeleteConfirm(null)}
                                className="text-gray-600 underline"
                              >
                                No
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Content Pages Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Contenido de Páginas
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link
              to="/admin/content/eures"
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition flex items-start gap-4"
            >
              <div className="bg-green-100 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-800">Página EURES</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Editar textos, títulos y contenido de la página de ayudas EURES
                </p>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Admin;